import { memo, forwardRef, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import { calculateDimensionsFromOutline } from '../../utils/dimensionCalculations';
import { calculatePolygonArea } from '../Editor/tools/PolygonTool';
import { useRenderCounter } from '../../hooks/useRenderCounter';

/**
 * GardenDimensionsPanel — isolated status panel for Wizard Step 3.
 *
 * Renders vertex count, open/closed status, and (when closed) bounding box +
 * area derived from the current polygon outline.
 *
 * Live-update model (#454):
 *   - During vertex drag, Step3 calls `panelRef.current.tick()` from a
 *     requestAnimationFrame inside the Fabric.js `object:moving` handler.
 *     The tick triggers an internal state bump, which re-renders only this
 *     component (not the whole Step3GardenOutline tree).
 *   - On `modified` (mouse-up) Step3 commits `setVertices` upstream; the new
 *     `vertices` prop reference flows through React.memo and re-renders here.
 *   - Dimensions are always read from `verticesRef.current` because Step3
 *     mutates that ref live in the `moving` handler before the React state
 *     catches up.
 */
const GardenDimensionsPanel = memo(forwardRef(function GardenDimensionsPanel(
  { vertices, verticesRef, isClosed, calibrationScale },
  ref
) {
  const [, setTick] = useState(0);

  useImperativeHandle(ref, () => ({
    tick: () => setTick((t) => (t + 1) % 1_000_000),
  }), []);

  useRenderCounter('GardenDimensionsPanel');

  // Read from ref so values stay correct during drag-ticks where the React
  // `vertices` prop has not yet been updated. Between drags, ref and prop match.
  const liveVerts = verticesRef?.current ?? vertices;
  const dimensions =
    liveVerts.length >= 3 && isClosed
      ? calculateDimensionsFromOutline(
          liveVerts,
          calibrationScale,
          calculatePolygonArea
        )
      : null;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Eckpunkte:</span>
        <span className="text-sm font-bold text-gray-900">{vertices.length}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Status:</span>
        <span
          className={`text-sm font-bold ${isClosed ? 'text-emerald-600' : 'text-gray-400'}`}
        >
          {isClosed ? '✓ Geschlossen' : 'Offen'}
        </span>
      </div>
      {dimensions && (
        <>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Umgrenzung:</span>
            <span className="text-sm font-bold text-emerald-600">
              {dimensions.widthMeters}m × {dimensions.lengthMeters}m
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Fläche:</span>
            <span className="text-sm font-bold text-emerald-600">
              {dimensions.areaSquareMeters} m²
            </span>
          </div>
        </>
      )}
    </div>
  );
}));

GardenDimensionsPanel.propTypes = {
  vertices: PropTypes.arrayOf(
    PropTypes.shape({ x: PropTypes.number, y: PropTypes.number })
  ).isRequired,
  verticesRef: PropTypes.shape({ current: PropTypes.array }),
  isClosed: PropTypes.bool.isRequired,
  calibrationScale: PropTypes.number,
};

export default GardenDimensionsPanel;
